import type {
  NameCheck,
  OrgAddress,
  OrgNewDraft,
  OrgNewStage,
} from '@/public/features/org/types'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/shared/lib/api'
import type { CreateOrgBody, CreateSolutionCardBody } from '@/shared/lib/apiTypes'
import { getSessionId } from '@/shared/lib/session'
import { toggle } from '@/shared/lib/toggle'
import { PROFILE_TONES } from '@/shared/lib/profileTone'
import {
  CARD_ACCESS_MAX,
  CARD_FIRST_STEP,
  CARD_MAX,
  CONSENTS,
  INTRO_MAX,
  LOGO_MAX_BYTES,
  ORG_LAST_STEP,
  TOTAL_STEPS,
} from '@/public/features/org/constants/orgNew'

function pickLogoTone(): string {
  const i = Math.floor(Math.random() * PROFILE_TONES.length)
  return PROFILE_TONES[i] ?? PROFILE_TONES[0]
}

const PASSWORD_MIN = 5

export function passwordError(value: string): string | null {
  if (value.length === 0) return null
  if (!/[A-Za-z]/.test(value) || !/\d/.test(value)) return '영문과 숫자를 포함해 주세요.'
  if (value.length < PASSWORD_MIN) return '5자 이상으로 작성해 주세요.'
  return null
}

export function isPasswordValid(value: string): boolean {
  return value.length >= PASSWORD_MIN && passwordError(value) === null
}

const EMPTY_ADDRESS: OrgAddress = {
  text: '',
  detail: '',
  regionCode: '',
  legalCode: '',
  regionName: '',
  legalName: '',
  postalCode: '',
  lat: null,
  lng: null,
}

function stepPath(n: number): string {
  return n <= 1 ? '/orgs/new' : `/orgs/new/${n}`
}

function digitsOf(value: string): string {
  return value.replace(/\D/g, '')
}

export function formatBizRegNo(value: string): string {
  const d = digitsOf(value).slice(0, 10)
  if (d.length <= 3) return d
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`
}

export function formatPhone(value: string): string {
  const d = digitsOf(value).slice(0, 11)
  const head = d.startsWith('02') ? 2 : 3
  if (d.length <= head) return d
  // 남은 자리가 8 이상이면 가운데가 넷이다(02-1234-5678 · 010-1234-5678).
  const mid = head + (d.length - head >= 8 ? 4 : 3)
  if (d.length <= mid) return `${d.slice(0, head)}-${d.slice(head)}`
  return `${d.slice(0, head)}-${d.slice(head, mid)}-${d.slice(mid)}`
}

export function isEmailValid(value: string): boolean {
  return value.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

export function useOrgNewDraft(step: number): OrgNewDraft {
  const navigate = useNavigate()

  const [orgTypeCode, setOrgTypeCode] = useState('')
  const [name, setNameValue] = useState('')
  const [nameCheck, setNameCheck] = useState<NameCheck>('idle')
  const [address, setAddress] = useState<OrgAddress>(EMPTY_ADDRESS)
  const [bizRegNo, setBizRegNoValue] = useState('')
  const [mainPhone, setMainPhoneValue] = useState('')
  const [logo, setLogoValue] = useState<{ dataUrl: string; name: string; file: File } | null>(null)
  const [logoError, setLogoError] = useState<string | null>(null)
  // 초기화 함수로 넘겨 **마운트 때 한 번만** 뽑는다(매 렌더 다시 뽑히지 않는다).
  const [logoTone] = useState(pickLogoTone)
  const [intro, setIntroValue] = useState('')
  const [mission, setMissionValue] = useState('')
  const [vision, setVisionValue] = useState('')
  const [homepage, setHomepage] = useState('')
  const [cardTitle, setCardTitleValue] = useState('')
  const [cardSummary, setCardSummaryValue] = useState('')
  const [cardProblem, setCardProblemValue] = useState('')
  const [cardBody, setCardBodyValue] = useState('')
  const [cardAccess, setCardAccessValue] = useState('')
  const [cardCategoryCodes, setCardCategoryCodes] = useState<string[]>([])
  const [cardTargetCodes, setCardTargetCodes] = useState<string[]>([])
  const [cardHelpCodes, setCardHelpCodes] = useState<string[]>([])
  const [cardResourceCodes, setCardResourceCodes] = useState<string[]>([])
  const [cardAreaCodes, setCardAreaCodes] = useState<string[]>([])
  const [cardCostType, setCardCostType] = useState('')
  const [cardPilot, setCardPilot] = useState('')
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(null)
  const [cardToken, setCardToken] = useState<string | null>(null)
  const [cardCount, setCardCount] = useState(0)
  /**
   * 이 흐름에서 **방금 만든** 해결카드들. 완료 화면이 이것을 그린다.
   * 서버에서 다시 읽을 수 없다 — `GET /api/orgs/:id` 는 `is_published` 인 기업만 주고
   * 카드도 `review_status = 'published'` 만 준다. 갓 등록한 기업·카드는 승인 전이라
   * 그 라우트가 404 를 낸다(실측: `apps/api/src/routes/org.ts`).
   */
  const [createdCards, setCreatedCards] = useState<{ title: string; categoryCodes: string[] }[]>([])
  const [managerName, setManagerName] = useState('')
  const [managerPhone, setManagerPhoneValue] = useState('')
  const [managerEmail, setManagerEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [consents, setConsents] = useState<string[]>([])

  const mutation = useMutation({
    mutationFn: async (input: {
      body: CreateOrgBody | null
      logoFile: File | null
      card: Omit<CreateSolutionCardBody, 'cardToken'> | null
      orgId: string | null
    }): Promise<{ id: string; cardToken: string | null }> => {
      if (input.orgId !== null) {
        if (input.card !== null) {
          if (cardToken === null) throw new Error('해결카드 등록 자격이 없습니다')
          await api.createSolutionCard({ ...input.card, cardToken })
        }
        return { id: input.orgId, cardToken: null }
      }
      if (input.body === null) throw new Error('기업 등록 본문이 없습니다')
      const logoId =
        input.logoFile === null ? undefined : (await api.uploadOrgLogo(input.logoFile)).logoId
      return api.createOrg(logoId === undefined ? input.body : { ...input.body, logoId })
    },
    onSuccess: (created, variables) => {
      if (variables.orgId === null) {
        setCreatedOrgId(created.id)
        setCardToken(created.cardToken)
      }
      if (variables.card !== null) {
        setCardCount((n) => n + 1)
        const card = variables.card
        setCreatedCards((prev) => [
          ...prev,
          { title: card.title, categoryCodes: card.categoryCodes },
        ])
      }
    },
  })

  const setOrgType = useCallback((code: string) => {
    setOrgTypeCode((prev) => (prev === code ? '' : code))
  }, [])

  const setName = useCallback((v: string) => {
    setNameValue(v)
    setNameCheck('idle')
  }, [])

  const checkName = useCallback(() => {
    const trimmed = name.trim()
    if (trimmed.length === 0) return
    setNameCheck('checking')
    api
      .orgNameAvailable(trimmed)
      .then((r) => setNameCheck(r.available ? 'ok' : 'taken'))
      // 못 물어봤으면 «쓸 수 있다»고 답하지 않는다 — 겹친 이름이 그대로 올라간다.
      .catch(() => setNameCheck('idle'))
  }, [name])

  const setBizRegNo = useCallback((v: string) => {
    setBizRegNoValue(formatBizRegNo(v))
  }, [])

  const setMainPhone = useCallback((v: string) => {
    setMainPhoneValue(formatPhone(v))
  }, [])

  const setManagerPhone = useCallback((v: string) => {
    setManagerPhoneValue(formatPhone(v))
  }, [])

  const setLogo = useCallback((file: File | null) => {
    setLogoError(null)
    if (file === null) {
      setLogoValue(null)
      return
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLogoError('이미지는 2MB 이하로 올려주세요.')
      return
    }
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string')
        setLogoValue({ dataUrl: reader.result, name: file.name, file })
    })
    reader.addEventListener('error', () => setLogoError('이미지를 읽지 못했습니다.'))
    reader.readAsDataURL(file)
  }, [])

  const setIntro = useCallback((v: string) => {
    setIntroValue(v.slice(0, INTRO_MAX))
  }, [])

  const setMission = useCallback((v: string) => {
    setMissionValue(v.slice(0, INTRO_MAX))
  }, [])

  const setVision = useCallback((v: string) => {
    setVisionValue(v.slice(0, INTRO_MAX))
  }, [])

  const toggleConsent = useCallback((key: string) => {
    setConsents((prev) => toggle(prev, key))
  }, [])

  const passwordMessage = passwordError(password)
  const hasConfirmError = passwordConfirm.length > 0 && passwordConfirm !== password

  const isStepDone = (n: number): boolean => {
    switch (n) {
      case 1:
        return orgTypeCode !== ''
      case 2:
        // 이름은 **중복 확인까지** 끝나야 한다(시안 오류 «중복 확인이 필요합니다»).
        return (
          name.trim().length > 0 &&
          nameCheck === 'ok' &&
          address.text.trim().length > 0 &&
          // 행정동 코드가 서버 필수값이다 — 주소를 검색해 고른 뒤라야 채워진다.
          address.regionCode !== ''
        )
      case 3:
        return intro.trim().length > 0
      case 4:
        return (
          isPasswordValid(password) &&
          passwordConfirm === password &&
          isEmailValid(managerEmail) &&
          CONSENTS.every((c) => consents.includes(c.key))
        )
      case 5:
        return (
          cardTitle.trim().length > 0 &&
          cardSummary.trim().length > 0 &&
          cardProblem.trim().length > 0 &&
          cardBody.trim().length > 0
        )
      case 6:
        return cardCategoryCodes.length > 0
      case 7:
        return cardTargetCodes.length > 0
      case 8:
        return cardHelpCodes.length > 0
      case 9:
        return cardResourceCodes.length > 0
      default:
        return cardAreaCodes.length > 0 && cardCostType !== '' && cardPilot !== ''
    }
  }

  const firstIncompleteStep = (() => {
    for (let n = 1; n <= TOTAL_STEPS; n += 1) {
      if (!isStepDone(n)) return n
    }
    return TOTAL_STEPS
  })()

  const isRangeDone = (from: number, to: number): boolean => {
    for (let n = from; n <= to; n += 1) {
      if (!isStepDone(n)) return false
    }
    return true
  }

  /**
   * 해결카드 단계(5~10)가 전부 끝났는지. PC 는 6~10 을 한 화면에 묶어 **주소의 단계가 6 인
   * 채로** 제출까지 가므로 `canGoNext`(그 단계 하나만 본다)로는 판정할 수 없다.
   * `firstIncompleteStep` 도 쓸 수 없다 — 전부 끝나도 `TOTAL_STEPS` 를 돌려주기 때문이다.
   */
  const isCardComplete = isRangeDone(CARD_FIRST_STEP, TOTAL_STEPS)

  const canGoNext =
    step === ORG_LAST_STEP
      ? isRangeDone(1, ORG_LAST_STEP)
      : step === TOTAL_STEPS
        ? isRangeDone(CARD_FIRST_STEP, TOTAL_STEPS)
        : isStepDone(step)

  const goPrev = useCallback(() => {
    if (step <= 1) {
      navigate(-1)
      return
    }
    navigate(stepPath(step - 1))
  }, [navigate, step])

  const goNext = useCallback(() => {
    if (step < TOTAL_STEPS) navigate(stepPath(step + 1))
  }, [navigate, step])

  const startNextCard = useCallback(() => {
    setCardTitleValue('')
    setCardSummaryValue('')
    setCardProblemValue('')
    setCardBodyValue('')
    setCardAccessValue('')
    setCardCategoryCodes([])
    setCardTargetCodes([])
    setCardHelpCodes([])
    setCardResourceCodes([])
    setCardAreaCodes([])
    setCardCostType('')
    setCardPilot('')
    mutation.reset()
    navigate(stepPath(CARD_FIRST_STEP))
  }, [mutation, navigate])

  const submit = useCallback(() => {
    if (!canGoNext || mutation.isPending) return

    if (step <= ORG_LAST_STEP && createdOrgId !== null) {
      navigate(stepPath(CARD_FIRST_STEP))
      return
    }

    if (step > ORG_LAST_STEP) {
      if (createdOrgId === null) return
      mutation.mutate({
        body: null,
        logoFile: null,
        orgId: createdOrgId,
        card: {
          title: cardTitle.trim(),
          summary: cardSummary.trim(),
          problemSolved: cardProblem.trim(),
          body: cardBody.trim(),
          ...(cardAccess.trim().length > 0 ? { accessCondition: cardAccess.trim() } : {}),
          categoryCodes: cardCategoryCodes,
          targetCodes: cardTargetCodes,
          helpTypeCodes: cardHelpCodes,
          resourceCodes: cardResourceCodes,
          serviceAreaCodes: cardAreaCodes,
          costType: cardCostType,
          pilotAvailable: cardPilot,
        },
      })
      return
    }

    const sessionId = getSessionId()
    const detail = address.detail.trim()
    const phone = mainPhone.trim()
    const introText = intro.trim()
    const email = managerEmail.trim()

    const body: CreateOrgBody = {
      orgTypeCode,
      name: name.trim(),
      address: {
        text: address.text.trim(),
        adminCode: address.regionCode,
        adminName: address.regionName,
        ...(detail.length > 0 ? { detail } : {}),
        ...(address.legalCode !== '' ? { legalCode: address.legalCode } : {}),
        ...(address.legalName !== '' ? { legalName: address.legalName } : {}),
        ...(address.postalCode !== '' ? { postalCode: address.postalCode } : {}),
      },
      bizRegNo: bizRegNo.trim(),
      managerName: managerName.trim(),
      managerPhone: managerPhone.trim(),
      // 값 자체를 보낸다(서버가 해시) — 「설정했다」로는 수정 인증을 만들 수 없다(인계서 §3).
      editPassword: password,
      consents: consents as CreateOrgBody['consents'],
      ...(phone.length > 0 ? { mainPhone: phone } : {}),
      ...(introText.length > 0 ? { intro: introText } : {}),
      ...(mission.trim().length > 0 ? { mission: mission.trim() } : {}),
      ...(vision.trim().length > 0 ? { vision: vision.trim() } : {}),
      ...(email.length > 0 ? { managerEmail: email } : {}),
      ...(sessionId !== undefined ? { sessionId } : {}),
    }
    mutation.mutate({ body, logoFile: logo?.file ?? null, card: null, orgId: null })
  }, [
    step,
    createdOrgId,
    navigate,
    canGoNext,
    mutation,
    orgTypeCode,
    name,
    address.text,
    address.detail,
    bizRegNo,
    mainPhone,
    logo,
    intro,
    mission,
    vision,
    managerName,
    managerPhone,
    managerEmail,
    password,
    consents,
    // 첫 해결카드(6·7·8단계) — 이 값들이 바뀌면 제출 본문도 바뀐다.
    cardTitle,
    cardSummary,
    cardProblem,
    cardBody,
    cardAccess,
    cardCategoryCodes,
    cardTargetCodes,
    cardHelpCodes,
    cardResourceCodes,
    cardAreaCodes,
    cardCostType,
    cardPilot,
    createdOrgId,
  ])

  const stage: OrgNewStage = !mutation.isSuccess
    ? 'form'
    : mutation.variables.card !== null
      ? 'card-done'
      : 'org-done'

  return {
    step,
    stage,
    createdOrgId,
    isSubmitting: mutation.isPending,
    error: mutation.error,

    orgTypeCode,
    name,
    nameCheck,
    address,
    bizRegNo,
    mainPhone,
    logo,
    logoError,
    logoTone,
    intro,
    mission,
    vision,
    homepage,
    managerName,
    managerPhone,
    managerEmail,
    password,
    passwordConfirm,
    consents,

    setOrgType,
    setName,
    checkName,
    setAddress,
    setBizRegNo,
    setMainPhone,
    setLogo,
    setIntro,
    setMission,
    setVision,
    setHomepage,
    cardTitle,
    cardSummary,
    cardProblem,
    cardBody,
    cardAccess,
    cardCategoryCodes,
    cardTargetCodes,
    setCardTitle: setCardTitleValue,
    setCardSummary: setCardSummaryValue,
    setCardProblem: setCardProblemValue,
    setCardBody: setCardBodyValue,
    setCardAccess: (v: string) => setCardAccessValue(v.slice(0, CARD_ACCESS_MAX)),
    toggleCardCategory: (code: string) =>
      setCardCategoryCodes((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
      ),
    toggleCardTarget: (code: string) =>
      setCardTargetCodes((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
      ),

    cardHelpCodes,
    cardResourceCodes,
    cardAreaCodes,
    cardCostType,
    cardPilot,
    toggleCardHelp: (code: string) =>
      setCardHelpCodes((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
      ),
    toggleCardResource: (code: string) =>
      setCardResourceCodes((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
      ),
    selectCardArea: (code: string) =>
      setCardAreaCodes((prev) => (prev[0] === code ? [] : [code])),
    selectCardCost: (code: string) => setCardCostType((prev) => (prev === code ? '' : code)),
    selectCardPilot: (code: string) => setCardPilot((prev) => (prev === code ? '' : code)),

    cardCount,
    canMakeMoreCards: cardCount < CARD_MAX,
    startNextCard,

    setManagerName,
    setManagerPhone,
    setManagerEmail,
    setPassword,
    setPasswordConfirm,
    toggleConsent,

    canGoNext,
    passwordMessage,
    hasConfirmError,
    firstIncompleteStep,
    isCardComplete,
    createdCards,
    goPrev,
    goNext,
    submit,
  }
}
